import React, { useEffect, useState, useMemo } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { usePermisos } from '../../hooks/usePermisos';
import {
  ClipboardList, Save, FlaskConical, RotateCcw, Loader2, AlertTriangle,
  CheckCircle2, XCircle, Info,
} from 'lucide-react';

const OP_LABEL = {
  gt: '>', gte: '≥', lt: '<', lte: '≤', eq: '=', neq: '≠', in: '∈', not_in: '∉',
};
const FUENTE_LABEL = { bcra: 'BCRA', pyp: 'PyP', nosis: 'Nosis', afip: 'AFIP', calc: 'Calc' };

const esArrayOp = (op) => op === 'in' || op === 'not_in';

const valorToStr = (valor) => {
  if (Array.isArray(valor)) return valor.join(', ');
  return valor === null || valor === undefined ? '' : String(valor);
};

const strToValor = (str, op) => {
  const s = (str ?? '').trim();
  if (esArrayOp(op)) {
    const parts = s.split(',').map((x) => x.trim()).filter((x) => x !== '');
    return parts.map((p) => (p !== '' && !isNaN(Number(p)) ? Number(p) : p));
  }
  if (s !== '' && !isNaN(Number(s))) return Number(s);
  return s;
};

const sameValor = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const ReglasEditor = () => {
  const { isAdmin } = usePermisos();
  const admin = isAdmin();
  const [reglas, setReglas] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/motor/reglas');
      const list = r.data.reglas || [];
      setReglas(JSON.parse(JSON.stringify(list)));
      setOriginal(JSON.parse(JSON.stringify(list)));
    } catch (e) {
      toast.error('No se pudieron cargar las reglas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setCampo = (codigo, campo, value) => {
    setReglas((prev) => prev.map((r) => (r.codigo === codigo ? { ...r, [campo]: value } : r)));
  };

  const dirtyCodigos = useMemo(() => {
    const om = Object.fromEntries(original.map((r) => [r.codigo, r]));
    return reglas.filter((r) => {
      const o = om[r.codigo];
      if (!o) return false;
      return (
        r.activa !== o.activa ||
        !sameValor(r.valor, o.valor) ||
        r.severidad !== o.severidad ||
        r.mensaje !== o.mensaje
      );
    }).map((r) => r.codigo);
  }, [reglas, original]);

  const guardar = async () => {
    if (!admin) { toast.error('Solo un administrador puede editar el reglamento'); return; }
    if (dirtyCodigos.length === 0) { toast.info('No hay cambios para guardar'); return; }
    setSaving(true);
    try {
      for (const codigo of dirtyCodigos) {
        const r = reglas.find((x) => x.codigo === codigo);
        await api.put(`/motor/reglas/${codigo}`, {
          activa: r.activa, valor: r.valor, severidad: r.severidad, mensaje: r.mensaje,
        });
      }
      toast.success(`Guardado (${dirtyCodigos.length} regla(s))`);
      await load();
      setSim(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const simular = async () => {
    setSimLoading(true);
    try {
      const r = await api.post('/motor/reglas/simular', { reglas, limit: 100 });
      setSim(r.data);
    } catch (e) {
      toast.error('No se pudo simular');
    } finally {
      setSimLoading(false);
    }
  };

  const restaurar = async () => {
    if (!admin) { toast.error('Solo un administrador'); return; }
    if (!window.confirm('¿Restaurar las 12 reglas por defecto? Se sobrescriben los valores actuales.')) return;
    try {
      await api.post('/motor/reglas/seed?force=true');
      toast.success('Reglamento restaurado');
      await load();
      setSim(null);
    } catch (e) {
      toast.error('No se pudo restaurar');
    }
  };

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="reglas-editor">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><ClipboardList className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Reglamento</h1>
            <p className="text-sm text-slate-500">Prendé/apagá y ajustá las reglas del motor sin tocar código</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={simular} disabled={simLoading} data-testid="reglas-simular-btn">
            {simLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
            Simular impacto
          </Button>
          {admin && (
            <>
              <Button variant="outline" size="sm" onClick={restaurar} data-testid="reglas-restaurar-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Restaurar
              </Button>
              <Button size="sm" onClick={guardar} disabled={saving || dirtyCodigos.length === 0} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="reglas-guardar-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar{dirtyCodigos.length > 0 ? ` (${dirtyCodigos.length})` : ''}
              </Button>
            </>
          )}
        </div>
      </div>

      {!admin && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <Info className="h-4 w-4" /> Modo lectura: solo un administrador puede guardar cambios. Podés simular impacto igual.
        </div>
      )}

      {/* Panel de simulación */}
      {sim && (
        <Card className="p-4 mb-6 border-teal-200 bg-teal-50/40" data-testid="reglas-sim-result">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-teal-600" />
              <span className="text-sm font-semibold text-slate-700">Impacto sobre {sim.evaluadas} decisiones guardadas:</span>
            </div>
            <span className="flex items-center gap-1 text-emerald-700 text-sm"><CheckCircle2 className="h-4 w-4" /> {sim.aprobar} aprobar</span>
            <span className="flex items-center gap-1 text-rose-700 text-sm"><XCircle className="h-4 w-4" /> {sim.rechazar} rechazar</span>
            <Badge variant="outline" className="border-amber-300 text-amber-700">{sim.total_cambios} cambiarían</Badge>
          </div>
          {sim.cambios?.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto text-xs space-y-1">
              {sim.cambios.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-600">
                  <span className="font-mono">{c.cuit}</span>
                  <span className={c.antes === 'Aprobar' ? 'text-emerald-600' : 'text-rose-600'}>{c.antes}</span>
                  <span>→</span>
                  <span className={c.ahora === 'Aprobar' ? 'text-emerald-600' : 'text-rose-600'}>{c.ahora}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <tr>
                  <th className="text-center px-3 py-2.5 font-medium w-16">Activa</th>
                  <th className="text-left px-3 py-2.5 font-medium">Regla</th>
                  <th className="text-left px-3 py-2.5 font-medium">Condición</th>
                  <th className="text-left px-3 py-2.5 font-medium w-40">Valor</th>
                  <th className="text-center px-3 py-2.5 font-medium w-28">Severidad</th>
                </tr>
              </thead>
              <tbody>
                {reglas.map((r) => {
                  const isDirty = dirtyCodigos.includes(r.codigo);
                  return (
                    <tr
                      key={r.codigo}
                      className={`border-t border-slate-100 dark:border-slate-800 ${isDirty ? 'bg-amber-50/50' : ''} ${!r.activa ? 'opacity-60' : ''}`}
                      data-testid={`regla-row-${r.codigo}`}
                    >
                      <td className="px-3 py-3 text-center">
                        <Switch
                          checked={!!r.activa}
                          onCheckedChange={(v) => setCampo(r.codigo, 'activa', v)}
                          disabled={!admin}
                          data-testid={`regla-toggle-${r.codigo}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-800 dark:text-slate-100">{r.nombre}</p>
                        <p className="text-xs text-slate-400 font-mono">{r.codigo}</p>
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                          <Badge variant="outline" className="text-[10px]">{FUENTE_LABEL[r.fuente] || r.fuente}</Badge>
                          <span className="font-mono text-xs">{r.campo}</span>
                          <span className="font-bold text-teal-600">{OP_LABEL[r.operador] || r.operador}</span>
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <Input
                          value={valorToStr(r.valor)}
                          onChange={(e) => setCampo(r.codigo, 'valor', strToValor(e.target.value, r.operador))}
                          disabled={!admin}
                          className="h-8 text-sm"
                          data-testid={`regla-valor-${r.codigo}`}
                        />
                        {esArrayOp(r.operador) && <p className="text-[10px] text-slate-400 mt-1">separar con comas</p>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          disabled={!admin}
                          onClick={() => setCampo(r.codigo, 'severidad', r.severidad === 'bloqueo' ? 'warning' : 'bloqueo')}
                          className="disabled:cursor-default"
                          title={admin ? 'Cambiar severidad' : ''}
                        >
                          {r.severidad === 'bloqueo'
                            ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0"><AlertTriangle className="h-3 w-3 mr-1" />Bloqueo</Badge>
                            : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">Warning</Badge>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ReglasEditor;
